import {
  Children,
  Fragment,
  isValidElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { ChevronDown } from "lucide-react";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { SELECT_LIST_GAP, useSelectListBox } from "./useSelectListBox";

type SelectOption = {
  value: string;
  label: string;
  disabled: boolean;
};

type SelectChangeEvent = {
  target: { value: string };
  currentTarget: { value: string };
};

type SelectInputProps = {
  value?: string | number | readonly string[];
  onChange?: (event: SelectChangeEvent) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  small?: boolean;
};

export const SelectRoot = styled.div<{ $small?: boolean }>`
  position: relative;
  display: inline-block;
  width: 100%;
  vertical-align: top;
  font-size: ${({ $small }) => ($small ? fontSizes.medium : fontSizes.regular)}px;
  box-sizing: border-box;
`;

const Trigger = styled.button<{ $open: boolean; $small?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  width: 100%;
  margin: 0;
  padding: ${({ $small }) =>
    $small
      ? `${spacing.x05 + 2}px ${spacing.x1}px`
      : `${spacing.x2}px ${spacing.x3}px ${spacing.x2}px ${spacing.x2}px`};
  border: 1px solid
    ${({ $open }) => ($open ? colors.primary : colors.border)};
  border-radius: 4px;
  font-size: inherit;
  font-family: inherit;
  line-height: inherit;
  color: ${colors.text};
  background-color: ${colors.backgroundLight};
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }

  &:disabled {
    background-color: ${colors.backgroundDark};
    color: ${colors.textMuted};
    cursor: not-allowed;
    opacity: 0.8;
  }
`;

const TriggerLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SelectChevron = styled(ChevronDown)<{ $open: boolean }>`
  flex-shrink: 0;
  color: ${colors.textMuted};
  pointer-events: none;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

export const SelectList = styled.ul<{
  $top: number;
  $left: number;
  $width: number;
  $maxHeight: number;
  $small?: boolean;
}>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  ${({ $width, $small }) =>
    $small
      ? `min-width: ${$width}px; width: max-content; max-width: calc(100vw - ${2 * SELECT_LIST_GAP}px);`
      : `width: ${$width}px;`}
  z-index: 1100;
  margin: 0;
  padding: ${spacing.x05}px 0;
  max-height: ${({ $maxHeight }) => $maxHeight}px;
  overflow-y: auto;
  list-style: none;
  background-color: ${colors.backgroundLight};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
`;

export const SelectOptionButton = styled.button<{
  $active: boolean;
  $selected: boolean;
  $disabled?: boolean;
  $small?: boolean;
}>`
  display: block;
  width: 100%;
  padding: ${({ $small }) =>
    $small ? `${spacing.x05}px ${spacing.x1}px` : `${spacing.x1}px ${spacing.x2}px`};
  ${({ $small }) => $small && "white-space: nowrap;"}
  border: none;
  background-color: ${({ $active, $disabled }) =>
    $disabled
      ? "transparent"
      : $active
        ? colors.backgroundDark
        : "transparent"};
  color: ${({ $disabled }) =>
    $disabled ? colors.textMuted : colors.text};
  font-size: ${({ $small }) => ($small ? fontSizes.medium : fontSizes.regular)}px;
  font-family: inherit;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};

  &:hover {
    background-color: ${({ $disabled }) =>
      $disabled ? "transparent" : colors.backgroundDark};
  }
`;

export const SelectEmpty = styled.li`
  padding: ${spacing.x1}px ${spacing.x2}px;
  color: ${colors.textMuted};
  font-size: ${fontSizes.small}px;
`;

const HiddenSelect = styled.select`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const labelFromNode = (node: ReactNode): string => {
  if (node == null || typeof node === "boolean") {
    return "";
  }
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(labelFromNode).join("");
  }
  return "";
};

const collectOptions = (children: ReactNode, into: SelectOption[]): void => {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }
    if (child.type === "option") {
      const props = child.props as {
        value?: string | number;
        disabled?: boolean;
        children?: ReactNode;
      };
      const label = labelFromNode(props.children);
      into.push({
        value:
          props.value !== undefined && props.value !== null
            ? String(props.value)
            : label,
        label,
        disabled: Boolean(props.disabled),
      });
      return;
    }
    if (child.type === Fragment) {
      collectOptions(
        (child.props as { children?: ReactNode }).children,
        into,
      );
    }
  });
};

const nextEnabledIndex = (
  options: SelectOption[],
  from: number,
  direction: 1 | -1,
): number => {
  if (options.length === 0) {
    return 0;
  }
  let index = from;
  for (let step = 0; step < options.length; step += 1) {
    index = (index + direction + options.length) % options.length;
    if (!options[index]?.disabled) {
      return index;
    }
  }
  return from;
};

export const SelectInput = ({
  value,
  onChange,
  onBlur,
  disabled,
  required,
  id,
  name,
  className,
  style,
  children,
  small,
}: SelectInputProps) => {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listBox = useSelectListBox(open, triggerRef, listRef);

  const options = useMemo(() => {
    const collected: SelectOption[] = [];
    collectOptions(children, collected);
    return collected;
  }, [children]);

  const stringValue = value == null ? "" : String(value);
  const selected = options.find((option) => option.value === stringValue);
  const selectedLabel = selected?.label ?? "";

  const highlightedIndex = Math.min(
    activeIndex,
    Math.max(options.length - 1, 0),
  );

  const emitChange = (nextValue: string) => {
    onChange?.({
      target: { value: nextValue },
      currentTarget: { value: nextValue },
    });
  };

  const close = () => {
    setOpen(false);
    onBlur?.();
  };

  const select = (option: SelectOption) => {
    if (option.disabled) {
      return;
    }
    emitChange(option.value);
    setOpen(false);
    onBlur?.();
    triggerRef.current?.focus();
  };

  const openList = () => {
    if (disabled || open) {
      return;
    }
    const selectedIndex = options.findIndex(
      (option) => option.value === stringValue,
    );
    const initialIndex =
      selectedIndex >= 0 && !options[selectedIndex]?.disabled
        ? selectedIndex
        : options.findIndex((option) => !option.disabled);
    setActiveIndex(initialIndex >= 0 ? initialIndex : 0);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        listRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
      onBlur?.();
    };
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open, onBlur]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const option = listRef.current?.querySelector<HTMLElement>(
      `[data-select-index="${highlightedIndex}"]`,
    );
    option?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, open]);

  const activeOption = options[highlightedIndex];

  return (
    <SelectRoot
      ref={rootRef}
      className={className}
      style={style}
      $small={small}
    >
      <Trigger
        ref={triggerRef}
        $small={small}
        id={id}
        type="button"
        role="combobox"
        $open={open}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={
          open && activeOption
            ? `${listId}-${highlightedIndex}`
            : undefined
        }
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          openList();
        }}
        onBlur={(event) => {
          if (
            rootRef.current?.contains(event.relatedTarget as Node) ||
            listRef.current?.contains(event.relatedTarget as Node)
          ) {
            return;
          }
          if (open) {
            close();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            if (!open) {
              openList();
              return;
            }
            setActiveIndex((index) => nextEnabledIndex(options, index, 1));
            return;
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            if (!open) {
              openList();
              return;
            }
            setActiveIndex((index) => nextEnabledIndex(options, index, -1));
            return;
          }
          if (event.key === "Home") {
            event.preventDefault();
            const first = options.findIndex((option) => !option.disabled);
            if (first >= 0) {
              if (!open) {
                openList();
              }
              setActiveIndex(first);
            }
            return;
          }
          if (event.key === "End") {
            event.preventDefault();
            const last = [...options]
              .reverse()
              .findIndex((option) => !option.disabled);
            if (last >= 0) {
              if (!open) {
                openList();
              }
              setActiveIndex(options.length - 1 - last);
            }
            return;
          }
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (!open) {
              openList();
              return;
            }
            if (activeOption) {
              select(activeOption);
            }
            return;
          }
          if (event.key === "Escape" && open) {
            event.preventDefault();
            setOpen(false);
          }
        }}
      >
        <TriggerLabel>{selectedLabel}</TriggerLabel>
        <SelectChevron size={14} $open={open} />
      </Trigger>
      {(name || required) && (
        <HiddenSelect
          name={name}
          required={required}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden
          value={stringValue}
          onChange={() => undefined}
        >
          {options.map((option) => (
            <option
              key={option.value || "empty"}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </HiddenSelect>
      )}
      {open &&
        createPortal(
          <SelectList
            ref={listRef}
            id={listId}
            role="listbox"
            $top={listBox.top}
            $left={listBox.left}
            $width={listBox.width}
            $maxHeight={listBox.maxHeight}
            $small={small}
          >
            {options.map((option, index) => (
              <li key={`${option.value}-${index}`} role="presentation">
                <SelectOptionButton
                  id={`${listId}-${index}`}
                  data-select-index={index}
                  type="button"
                  role="option"
                  $active={index === highlightedIndex}
                  $selected={option.value === stringValue}
                  $disabled={option.disabled}
                  $small={small}
                  aria-selected={option.value === stringValue}
                  aria-disabled={option.disabled}
                  onMouseEnter={() => {
                    if (!option.disabled) {
                      setActiveIndex(index);
                    }
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    select(option);
                  }}
                >
                  {option.label}
                </SelectOptionButton>
              </li>
            ))}
          </SelectList>,
          document.body,
        )}
    </SelectRoot>
  );
};
